import dayjs from 'dayjs'
import { FunctionComponent } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useRecoilState } from 'recoil'
import { useEnrichContentQuery, useGetContentByIdQuery } from '../api/client'
import ContentList from '../components/content/ContentList'
import ContentMetadataPanel from '../components/content/content-card/metadata/ContentMetadataPanel'
import ScreenshotAttemptsPanel from '../components/screenshots/ScreenshotAttemptsPanel'
import PageMasthead from '../components/PageMasthead'
import useContentSearchParams from '../hooks/useContentQueryParams'
import { isUserAdminState } from '../store/DomainStore'

/**
 * Displays one or more content items.
 *
 * Expects the contentIds to be on the URL path (not the params), as a comma-separated list.
 * e.g. /content/123 or /content/123,456,789
 *
 * Displays using the ContentList component, because that does all the heavy lifting.
 *
 * Has to fiddle with a few of the content search params to get the content loading,
 * but there's precedent for this in `SpamContentList`.
 */
const ContentPage: FunctionComponent = () => {
    const { contentId } = useParams<{ contentId: string }>()
    const [admin] = useRecoilState(isUserAdminState)

    // Parse comma-separated content IDs
    const contentIds = contentId
        ? contentId
              .split(',')
              .map((id) => Number(id.trim()))
              .filter((id) => !isNaN(id) && id > 0)
        : []

    const firstId = contentIds[0]

    const { searchParameters } = useContentSearchParams()
    if (contentIds.length > 0) {
        searchParameters.contentIds = contentIds
    }

    searchParameters.owned = undefined
    // Hack -- we know exactly what we're fetching but we won't get it unless we have a compatible time,
    // Lets make sure that the range is big enough...
    searchParameters.startDateTime = dayjs('2019-01-01')
    searchParameters.endDateTime = dayjs('3000-01-02')

    const { data } = useGetContentByIdQuery({
        variables: {
            id: firstId
        },
        skip: !firstId
    })

    const content = data?.getContentById

    // Enrich content for metadata panel (admin only)
    const contentsToEnrich = content
        ? [
              {
                  id: content.id,
                  accountId: content.account.id,
                  socialMediaServiceShortName: content.socialMediaServiceName,
                  serviceContentId: content.serviceContentId,
                  serviceAuthorId: content.serviceAuthorId,
                  serviceAuthorName: content.serviceAuthorName,
                  serviceAuthorUsername: content.serviceAuthorDisplayUsername,
                  originalPostLinkId: content.socialMediaContentLink,
                  originalPostId: content.originalPostId
              }
          ]
        : []

    const { data: enrichedContentResults } = useEnrichContentQuery({
        variables: { contents: contentsToEnrich },
        skip: !admin || !content
    })

    const enrichedContent = enrichedContentResults?.enrichContent?.find((e) => e.id === firstId)

    return (
        <div className="self-center w-full">
            {content ? (
                <PageMasthead
                    title={content.account.accountName || 'Content'}
                    subtitle={content.account.socialMediaServiceDisplayName || undefined}
                    actions={
                        <Link
                            to={`/account/${content.account.id}`}
                            className="text-sm font-medium text-arwen-600 hover:text-arwen-700"
                        >
                            View profile page
                        </Link>
                    }
                />
            ) : null}

            <ContentList
                searchParameters={searchParameters}
                showOwningAccount={true}
                enableBulkActions={false}
                hidePagination={contentIds.length <= 1}
            />

            {admin && content && (
                <div className="mt-6 px-2 space-y-6 pb-8">
                    <ContentMetadataPanel content={content} enrichedContent={enrichedContent} />
                    <ScreenshotAttemptsPanel contentId={content.id} />
                </div>
            )}
        </div>
    )
}

export default ContentPage
